import React from 'react';

/**
 * Pre-assign warning when slide narration audio is missing.
 *
 * Shown by VideoAssignmentView and the lesson-kit AssignWorksheetModal
 * before they fire the assign POST so a teacher / parent never sends a
 * silent deck out by accident. Three actions:
 *
 *   - Cancel              → back off entirely, no assign
 *   - Assign without audio → proceed; students see slides, no narration
 *   - Generate audio first → run /videos/{id}/audio, then assign
 */
const AudioMissingConfirm = ({
  missingCount,
  totalCount,
  generating = false,
  onGenerate,
  onSkip,
  onCancel,
}) => {
  const allMissing = missingCount >= totalCount;
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black bg-opacity-60 p-4">
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center text-xl">
            🔊
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-gray-900">
              Slide audio not ready
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              {allMissing
                ? 'No narration audio has been generated for the slides yet.'
                : `${missingCount} of ${totalCount} slides are missing narration audio.`}
              {' '}Students will see the slides but won&apos;t hear narration during playback.
            </p>
          </div>
        </div>
        <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onCancel}
            disabled={generating}
            className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSkip}
            disabled={generating}
            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-50"
          >
            Assign without audio
          </button>
          <button
            type="button"
            onClick={onGenerate}
            disabled={generating}
            className="rounded-lg bg-orange-500 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-600 disabled:opacity-50"
          >
            {generating ? 'Generating…' : 'Generate audio first'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AudioMissingConfirm;
